// Apolo IDE — useMentionsResolver (Sprint v11 E2).
//
// Wires the mentions library to Electron's file IPC, browser fetch (for
// @docs:), and the Worker grep endpoint (for @symbol: until semantic
// indexing in E7 lands). Returns a stable resolver function so callers
// can pass it straight to `resolveAndEnrich(text, io)`.

import { useCallback, useMemo } from 'react';
import { hasElectronBridge } from '../lib/electronBridge.js';
import { apiUrl } from '../lib/apiBase.js';
import { getStoredToken, apiFetch } from '../lib/api.js';

/**
 * Format a KnowledgeGraphV1 subgraph (already InjectionBudget-bounded by the
 * server) into a readable structural context block. Pure — exported for tests.
 */
export function formatSubgraph(subgraph, nodeId) {
  const nodes = subgraph?.nodes ?? [];
  const edges = subgraph?.edges ?? [];
  if (nodes.length === 0) return null;
  const focus = nodes.find((n) => n.id === nodeId);
  const header = focus ? `${focus.name} (${focus.type}, ${focus.layer}) @ ${focus.path}` : nodeId;
  const nodeLines = nodes.map((n) => `- ${n.name} (${n.type}) ${n.path}`).join('\n');
  const relLines = edges.map((e) => `- ${e.from} ${e.type} ${e.to}`).join('\n');
  return `Graph node: ${header}\n\nNeighborhood (${nodes.length} nodes, ${edges.length} edges):\n${nodeLines}${relLines ? `\n\nRelations:\n${relLines}` : ''}`;
}

/**
 * Resolve a graph-node @mention to its structural subgraph context via the
 * MEM1 unified retrieval endpoint (budget-bounded server-side). The server
 * resolves the node against the user's latest snapshot. Exported for tests.
 */
export async function resolveGraphNode(nodeId, { snapshotId } = {}) {
  if (!nodeId) return null;
  try {
    const res = await apiFetch('/api/memory/retrieve', {
      method: 'POST',
      body: { query: '', context: { node: nodeId, snapshotId } },
    });
    if (!res.ok) return null;
    return formatSubgraph(res.data?.graph_subgraph, nodeId);
  } catch {
    return null;
  }
}

async function fetchDocText(url) {
  try {
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return null;
    const ct = res.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const json = await res.json();
      return JSON.stringify(json, null, 2);
    }
    const text = await res.text();
    // Strip raw HTML tags so an LLM doesn't waste context on layout markup.
    // Naive but adequate for docs pages — the LLM still gets text content.
    if (ct.includes('text/html')) {
      return text.replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    }
    return text;
  } catch {
    return null;
  }
}

async function grepSymbol(sessionId, name) {
  if (!sessionId || !name) return null;
  const token = getStoredToken();
  try {
    const res = await fetch(apiUrl(`/api/code-ide/grep?session_id=${encodeURIComponent(sessionId)}&q=${encodeURIComponent(name)}`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    const body = await res.json();
    if (!Array.isArray(body?.results)) return null;
    return body.results
      .slice(0, 20)
      .map((r) => `${r.path}:${r.line}: ${r.text}`)
      .join('\n');
  } catch {
    return null;
  }
}

// Aba-Apolo C2 — `#chat:<id>`: busca a transcrição de uma conversa passada e a
// formata como bloco de contexto read-only (papéis + conteúdo, cap de itens).
// Privada — usada só pelo io.loadChat abaixo. Defensiva com o shape
// ({messages:[...]} | array). O contrato é coberto por mentions.test (chat).
async function loadChatTranscript(sessionId) {
  if (!sessionId) return null;
  const token = getStoredToken();
  try {
    const res = await fetch(apiUrl(`/api/code/sessions/${encodeURIComponent(sessionId)}/messages`), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) return null;
    const body = await res.json();
    const rows = Array.isArray(body) ? body : (Array.isArray(body?.messages) ? body.messages : []);
    if (rows.length === 0) return null;
    return rows
      .slice(-40)
      .map((r) => {
        const who = r.role === 'user' ? 'Usuário' : r.role === 'agent' ? 'Assistente' : (r.role || 'msg');
        const content = String(r.content || '').trim();
        return content ? `${who}: ${content}` : null;
      })
      .filter(Boolean)
      .join('\n\n');
  } catch {
    return null;
  }
}

/**
 * Build a stable IO object suitable for resolveMention / resolveAndEnrich.
 *
 * @param {{ root?: string, sessionId?: string }} options
 */
export function useMentionsResolver({ root, sessionId }) {
  const readFile = useCallback(async (path) => {
    if (!hasElectronBridge() || !root) return null;
    try {
      const res = await window.apolo.file.read({ root, filePath: path });
      return typeof res?.content === 'string' ? res.content : null;
    } catch {
      return null;
    }
  }, [root]);

  const listFolder = useCallback(async (path) => {
    if (!hasElectronBridge() || !root) return [];
    try {
      const res = await window.apolo.file.list({ root, dir: path });
      return Array.isArray(res) ? res : (Array.isArray(res?.entries) ? res.entries : []);
    } catch {
      return [];
    }
  }, [root]);

  const fetchUrl = useCallback(async (url) => fetchDocText(url), []);
  const grep = useCallback(async (name) => grepSymbol(sessionId, name), [sessionId]);
  const resolveNode = useCallback(async (nodeId) => resolveGraphNode(nodeId), []);
  const loadChat = useCallback(async (id) => loadChatTranscript(id), []);

  const io = useMemo(
    () => ({ readFile, listFolder, fetchUrl, grepSymbol: grep, resolveGraphNode: resolveNode, loadChat }),
    [readFile, listFolder, fetchUrl, grep, resolveNode, loadChat],
  );

  return io;
}
